import { ILayer } from '../layer.type';
import { environment } from '~environments/environment';
import { _ } from '~shared/utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';
import { ValmetDnaValue } from '~app/custom/neve/valmet-dna.service';
import { sum } from 'lodash';

const markerUrlBase = environment.assetRoot + '/data/T14/markers/';

const smallElementBaseZoom = 13;
const smallElementValveZoom = 12;
const smallElementsOtherZoom = 11;

const valveLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': [
    'step', ['zoom'],
    0,
    15, 10,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.28,
    16, 0.46,
  ],
};

const valvePaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
  'icon-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
  'text-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
};

const othersLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.45,
    16, 0.8,
  ],
};

const customerLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementBaseZoom, 0.1,
    16, 0.8,
  ],
};

const textLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-field': ['get', 'linkedText'],
  'text-size': 13,
  'text-radial-offset': 1,
  'text-anchor': 'top', // overwritten by some layers
  'text-max-width': 100, // prevent wrapping
};

const textPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
  'icon-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
  'text-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
};

/**
 * Neve
 */
export type TagPair = [position: string, tag: string];

// using this instead of null to enforce a type check
export type EmptyValmetDnaConfiguredTag = {
  isEmpty: true
};

// rename for clarity
export type ValmetDnaConfiguredTag = ValmetDnaTagConfig;
export type OptionalValmetDnaConfiguredTag = ValmetDnaConfiguredTag | EmptyValmetDnaConfiguredTag;


const defaultValueTransformer = (value: ValmetDnaValue): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: +value.value,
    formattedValue: value.value
  }
};

export const isEmptyValmetDnaConfiguredTag = (tag: OptionalValmetDnaConfiguredTag): tag is EmptyValmetDnaConfiguredTag => {
  return !!(tag as any).isEmpty;
};

const configureOptionalTags = (entries: (TagPair | ValmetDnaTagConfig | undefined)[]): (ValmetDnaConfiguredTag | EmptyValmetDnaConfiguredTag)[] => {
  return entries.map(entry => {
    if(!entry) {
      return {
        isEmpty: true
      } as EmptyValmetDnaConfiguredTag
    }

    return configureTag(entry);
  })
}

const configureTags = (entries: (TagPair | ValmetDnaTagConfig)[]): (ValmetDnaConfiguredTag)[] => {
  return entries.map(entry => configureTag(entry));
};

const configureTag = (entry: (TagPair | ValmetDnaTagConfig)): ValmetDnaConfiguredTag => {
  if(Array.isArray(entry)) {
    return {
      tagpair: entry,
      valueTransformer: defaultValueTransformer
    }
  };

  return entry;
};

export type ValmetDnaTransformedValue = ValmetDnaValue & {
  numericValue: number
  formattedValue?: string
};

export type ValmetDnaTagConfig = {
  tagpair: TagPair,
  valueTransformer?: (value: ValmetDnaValue) => ValmetDnaTransformedValue,
  minValue?: number;
  maxValue?: number;
};

const negativeValueTransformer = (value): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: -1 * (+value.value)
  }
};

export interface NeveTenantConfig extends TenantConfig {
  tenantCustomData: {
    /**
     * pressure.return, temperature.* tags are in the order specified in https://miproagile.atlassian.net/browse/MR-865
     * if order is changed, neve-measurements-table will show tags for wrong map locations!
     */
    valmetDnaTags: {
      siloSurface: ValmetDnaTagConfig[],
      pressure: {
        /**
         * Paine-ero
         */
        delta: ValmetDnaTagConfig[],
        /**
         * Menopaine
         */
        out: ValmetDnaTagConfig[],
        /**
         * Paluupaine
         */
        return: OptionalValmetDnaConfiguredTag[],
      },
      temperature: {
        /**
         * Menolämpö
         */
        out: OptionalValmetDnaConfiguredTag[],
        /**
         * Paluulämpö
         */
        return: OptionalValmetDnaConfiguredTag[],
      },
      power: {
        mainNetwork: ValmetDnaTagConfig[],
        auxiliaryNetwork: ValmetDnaTagConfig[],
        allNetworks: ValmetDnaTagConfig[],
      },
      electricity: ValmetDnaTagConfig[],
      outsideTemperature: ValmetDnaTagConfig[],
      pumpStatus: ValmetDnaTagConfig[],
      kpi: ValmetDnaTagConfig[],
    }
  }
};

const getPowerNetworkTags = () => {
  const mainNetwork = configureTags([
    ['01NDB10U901', 'av'],
    ['NDB10CU010',  'av'],
    ['NDB31CU001',  'av'],
    ['NDE11FU002',  'av'], // KL-AKKU PURKU KL-TEHO
    {
      tagpair: ['NDE11FU001',  'av'], // KL-AKKU LATAUS KL-TEHO
      valueTransformer: negativeValueTransformer
    },
    ['3NPU01',      'av'],
    ['5NDB20FN001', 'av'],
    ['10NPU01',     'av'],
    ['11ND20CU901', 'av'],
    ['17N20CU001',  'av'],
    ['00100WQ01VP', 'av'],
    ['LAOU01',      'av'],
  ]);

  const auxiliaryNetwork = configureTags([
    ['30N10CU001',  'av'],
    ['20NDA30CU001','av'],
    ['31NDB22CU001','av'],
  ]);

  return {
    mainNetwork,
    auxiliaryNetwork,
    allNetworks: mainNetwork.concat(auxiliaryNetwork)
  };
}

const getPressureTags = () => {
  return {
    delta: configureTags([
      ['NDB10CP901', 'av'],
      ['CYG26CP901', 'av'],
      ['RAB12CP902', 'av'],
      ['RAB12CP901', 'av'],
      ['CYG29CP901', 'av'],
      ['UM10PD01',   'av'],
      ['CYG25CP901', 'av'],
      ['CYG28CP901', 'av'],
      ['CYG30CP901', 'av'],
      ['16PD901',    'av'],
      ['00100PE00P', 'av'],
      ['17N10CP901', 'av'],
      ['06DP001',    'av'],
      ['UM10PD0401', 'av'],
      ['05000PE01P', 'av'],
      ['05100PE01P', 'av'],
    ]),
    out: configureTags([
      ['NDA30CP010', 'av'],
      ['CYG26CP001', 'av'],
      ['RAB12CP001', 'av'],
      ['RAB12CP002', 'av'],
      ['CYG29CP001', 'av'],
      ['CYG27CP001', 'av'],
      ['CYG25CP001', 'av'],
      ['CYG28CP001', 'av'],
      ['CYG30CP001', 'av'],
      ['16P001',     'av'],
      ['00100PE01P', 'av'],
      ['17N10CP001', 'av'],
      ['06P001',     'av'],
      ['UM10P0401',  'av'],
      ['05000PE01P', 'av'],
      ['05100PE01P', 'av'],
    ]),
    return: configureOptionalTags([
      ['NDB10CP010', 'av'],
      ['CYG26CP002', 'av'],
      undefined,
      undefined,
      ['CYG29CP002', 'av'],
      ['CYG27CP002', 'av'],
      ['CYG25CP002', 'av'],
      ['CYG28CP002', 'av'],
      ['CYG30CP002', 'av'],
      ['16P002',     'av'],
      ['00100PE02P', 'av'],
      ['17N20CP001', 'av'],
      ['06P002',     'av'],
      ['UM20P0401',  'av'],
      ['05000PE02P', 'av'],
      ['05100PE02P', 'av'],
    ]),
  };
};

const getTemperatureTags = () => {
  return {
    out: configureOptionalTags([
      ['1UN10CT007',  'av'],
      ['CYG26CT001',  'av'],
      ['RAB12CT001',  'av'],
      ['RAB12CT001',  'av'],
      ['CYG29CT001',  'av'],
      ['CYG27CT001',  'av'],
      ['CYG25CT001',  'av'],
      ['CYG28CT001',  'av'],
      ['CYG30CT001',  'av'],
      undefined,
      ['00100TE01P',  'av'],
      ['17N10CT001',  'av'],
      undefined,
      ['UM10T0401',   'av'],
      ['05000TE01P',  'av'],
      undefined
    ]),
    return: configureOptionalTags([
      ['NDB31CT001',  'av'],
      ['CYG26CT002',  'av'],
      ['UM20CT010',   'av'],
      ['UM20CT010',   'av'],
      ['CYG29CT002',  'av'],
      ['CYG27CT002',  'av'],
      ['CYG25CT002',  'av'],
      ['CYG28CT002',  'av'],
      ['CYG30CT002',  'av'],
      undefined,
      ['00100TE02P',  'av'],
      ['17N20CT001',  'av'],
      undefined,
      ['UM20T0401',   'av'],
      ['05000TE02P',  'av'],
      undefined,
    ]),
  }
};

const getTenantCustomData = (): NeveTenantConfig['tenantCustomData'] => {
  return {
    valmetDnaTags: {
      siloSurface: configureTags([
        ['END71CL001B', 'av'],
        ['ENE20CL005', 'av'],
      ]),
      pressure: getPressureTags(),
      temperature: getTemperatureTags(),
      power: getPowerNetworkTags(),
      electricity: configureTags([
        ['AQ011B67', 'av']
      ]),
      outsideTemperature: configureTags([
        ['1UN10T004', 'av']
      ]),
      pumpStatus: configureTags([
        ['P01D01', 's'],
        ['P01D02', 's'],
        ['RAB12AP001', 's'],
        ['17NDA01P001', 's'],
        ['17NDA01P002', 's'],
        ['02900PU01', 's'],
        ['02900PU02', 's'],
        ['00201PU01', 's'],
        ['00201PU02', 's'],
        ['05000PU03', 's'],
        ['05000PU04', 's'],
      ]),
      kpi: [
        {
          //	Tulipesän lämpötila
          tagpair: ['31HNA10CT001', 'av'],
          valueTransformer: defaultValueTransformer,
          minValue: 500,
          maxValue: 800,
        },
        {
          // Tulipesän paine
          tagpair: ['31HNA10CP001', 'av'],
          valueTransformer: defaultValueTransformer,
          minValue: -80,
          maxValue: -40,
        },
        {
          // Savukaasun happi
          tagpair: ['31HNA11CA001', 'av'],
          valueTransformer: defaultValueTransformer,
          minValue: 4,
          maxValue: 7,
        },
        // {
        //   // KL Paine-ero
        //   tagpair: ['31NDA30DP001', 'av'],
        //   valueTransformer: defaultValueTransformer,
        //   minValue: 1.3,
        //   maxValue: 2.0,
        // }
      ],
    }
  }
}

export default (): NeveTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 66.523,
    lng: 25.751,
  },
  defaultZoom: 10.82,
  images: [
    { id: 'asiakas', url: markerUrlBase + 'asiakas-Asiakas.png' },
    { id: 'asiakas-iso', url: markerUrlBase + 'asiakas-Asiakas_iso.png' },
    { id: 'lampolaitos-bio-on', url: markerUrlBase + 'running-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-on', url: markerUrlBase + 'running-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-on', url: markerUrlBase + 'running-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-on', url: markerUrlBase + 'running-lämpölaitos.png' },
    { id: 'voimalaitos-on', url: markerUrlBase + 'running-Voimalaitos.png' },
    { id: 'lampolaitos-bio-off', url: markerUrlBase + 'gray-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-off', url: markerUrlBase + 'gray-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-off', url: markerUrlBase + 'gray-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-off', url: markerUrlBase + 'gray-lämpölaitos.png' },
    { id: 'voimalaitos-off', url: markerUrlBase + 'gray-Voimalaitos.png' },
    { id: 'lampolaitos-bio-loading', url: markerUrlBase + 'fault-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-loading', url: markerUrlBase + 'fault-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-loading', url: markerUrlBase + 'fault-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-loading', url: markerUrlBase + 'fault-lämpölaitos.png' },
    { id: 'voimalaitos-loading', url: markerUrlBase + 'fault-Voimalaitos.png' },
    { id: 'lampolaitos-bio-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-unlinked', url: markerUrlBase + 'tentative-lämpölaitos.png' },
    { id: 'voimalaitos-unlinked', url: markerUrlBase + 'tentative-Voimalaitos.png' },
    { id: 'paineero-on', url: markerUrlBase + 'running-Paineeneromittauspiste.png' },
    { id: 'paineero-off', url: markerUrlBase + 'critical-Paineeneromittauspiste.png' },
    { id: 'paineero-loading', url: markerUrlBase + 'fault-Paineeneromittauspiste.png' },
    { id: 'paineero-unlinked', url: markerUrlBase + 'tentative-Paineeneromittauspiste.png' },
    { id: 'pumppuasema-on', url: markerUrlBase + 'running-Pumppuasema.png' },
    { id: 'pumppuasema-off', url: markerUrlBase + 'gray-Pumppuasema.png' },
    { id: 'pumppuasema-loading', url: markerUrlBase + 'fault-Pumppuasema.png' },
    { id: 'pumppuasema-unlinked', url: markerUrlBase + 'tentative-Pumppuasema.png' },
    { id: 'kpi-on', url: markerUrlBase + 'running-tachometer-right.png' },
    { id: 'kpi-off', url: markerUrlBase + 'critical-tachometer-left.png' },
    { id: 'kpi-loading', url: markerUrlBase + 'fault-tachometer-left.png' },
    { id: 'kpi-unlinked', url: markerUrlBase + 'tentative-tachometer-right.png' },
  ],
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }],
  tenantCustomData: getTenantCustomData(),
  useGlobalFlags: true,
  renderFlagFilteringForLayers: true
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T14.json by MapService.addLayer
// numbering for used layers refers to: https://miproagile.atlassian.net/browse/MR-789
export function getLayers(): ILayer[] {
  return [
    // 1. Kaukolämpöverkosto (Julkinen osa)
    {
      id: 'Putki_julkinen',
      vectorSourceLayer: 'Putki_julkinen',
      name: 'Kaukolämpöverkosto',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: [
          'interpolate', ['linear'], ['zoom'],
          0, 0.5,
          10, 1.25,
          22, 4
        ],
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Putki_pieni',
      vectorSourceLayer: 'Putki_pieni',
      name: 'Kaukolämpöverkosto (pienet putket)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: 1,
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 2. Ei-julkinen verkosto (Kaukolämpöverkoston osa, esim. Laji: DN150/2Mpuk_ei_julkinen)
    {
      id: 'Putki_ei_julkinen',
      vectorSourceLayer: 'Putki_ei_julkinen',
      name: 'Kaukolämpöverkosto (ei-julkinen)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#fd0150',
        strokeWeight: 1,
        visible: false,
      },
      mapboxOptions: {
        paint: {
          'line-gap-width': 2,
          'line-dasharray': [1, 1],
          'line-blur': 1
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 3. Laitokset
    {
      id: 'Syottopiste',
      name: 'Laitokset',
      orderingWeight: 5,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.power.allNetworks.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => {
        const total = sum(measurements.map(({value}) => parseFloat(value)));
        //// suosiola plant MW can be above 100
        return total < 0.01 ? null : `${total < 100 ? total.toFixed(2) : total.toFixed(1)} ${measurements[0].unit}`;
      },
      linkedAuxMeasurements: getTenantCustomData().valmetDnaTags.kpi,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: ['concat',
            [
              'case',
              ['==', ['get', 'Laji'], 'Voimalaitos'], 'voimalaitos',
              ['in', 'BIO', ['get', 'Polttoaine']], 'lampolaitos-bio',
              ['in', 'Turve', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'Hake', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'POK', ['get', 'Polttoaine']], 'lampolaitos-oil',
              ['in', 'POR', ['get', 'Polttoaine']], 'lampolaitos-oil',
              'lampolaitos',
            ],
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['>=', ['get', 'linkedMaxValue'], 0.01], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'text-size': 14,
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            smallElementValveZoom, 1,
            16, 1.2,
          ],
          'text-radial-offset': 1.5, // compensate for larger icon
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Aseman tai kaivon tunnus',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
          coordinates: false,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // (siilot)
    {
      id: 'Siilot',
      name: 'Siilot',
      orderingWeight: 7,
      belowRoadLayers: false,
      selectable: false,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.siloSurface.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({label, value, unit}) => `${label.replace(/\s+pinta 2$/i, '')}: ${Math.round(parseFloat(value))} ${unit}`
      ).join('\n'),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: false,
        icon: {
          url: markerUrlBase + 'running-Siilo.png',
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'text-anchor': [
            'coalesce',
            ['get', 'text-anchor'],
            'left',
          ],
          'icon-size': 0.75,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 4. Pumppaamot
    {
      id: 'Pumppuasema',
      name: 'Pumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.pumpStatus.map(i => i.tagpair),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'pumppuasema',
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['in', '|2|', ['get', 'linkedValues']], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: othersLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 5. Kaivot
    // TODO
    // 6. Porasulkuventtiilit (Löytyy kasettiventtiilin alta)
    {
      id: 'Porasulkuventtiili',
      vectorSourceLayer: 'Porasulkuventtiili',
      name: 'Porasulkuventtiilit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'PS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 7. Kiertolenkit (Löytyy kasettiventtiilin alta)
    {
      id: 'Kiertolenkki',
      vectorSourceLayer: 'Kiertolenkki_sv',
      name: 'Kiertolenkit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-kiertolenkki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...othersLayout,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 8. Elementtiventtiilit (alasvetovalikko)
    //    7 alaryhmää (Jäljelle jääneet osat kasettiventtiilin alta kun ottaa pois porasulkuventtiilit ja kiertolenkit).
    {
      id: 'Haaroitusventtiili_kaivossa',
      vectorSourceLayer: 'Haaroitusventtiili_kaivossa',
      name: 'Haaroitusventtiili kaivossa',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'HVK',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Kaivosulkuventtiili',
      vectorSourceLayer: 'Kaivosulkuventtiili',
      name: 'Kaivosulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'KS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Sulkuventtiili',
      vectorSourceLayer: 'Sulkuventtiili',
      name: 'Sulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'S',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_IV',
      vectorSourceLayer: 'SV_IV',
      name: 'SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_TV',
      vectorSourceLayer: 'SV_TV',
      name: 'SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'IV_SV_IV',
      vectorSourceLayer: 'IV_SV_IV',
      name: 'IV+SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'IV+SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'TV_SV_TV',
      vectorSourceLayer: 'TV_SV_TV',
      name: 'TV+SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'TV+SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 9. Liittymät
    {
      id: 'Liittyma',
      vectorSourceLayer: 'Liittyma',
      name: 'Liittymät',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          urlExpression: [
            'case',
            ['>=', ['number', ['get', 'Lämmitettävä rakennustilavuus (m³)'], 0], 100_000], 'asiakas-iso',
            'asiakas',
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        layout: customerLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 10. Verkostopisteet
    // TODO
    // 11. Paine-eromittauspisteet (Löytyy Solmupisteen alta, muut solmupisteet ei tarvitse näkyä)
    {
      id: 'Paine_eromittaus',
      name: 'Paine-eromittauspisteet',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.pressure.delta.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({value, unit}) => `${parseFloat(value).toFixed(1)} ${unit}`
      ).join('\n'),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'paineero',
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['>=', ['get', 'linkedMaxValue'], 0.5], 'on',
              'off',
            ]
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...othersLayout,
          ...textLayout,
          'text-anchor': [
            'coalesce',
            ['get', 'text-anchor'],
            'bottom',
          ],
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 12. Osoitenumerot
    getSharedLayer('houseNumbers'),
    // 13. Pinnanmuodot
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    // 14. Vesialueet
    getSharedLayer('groundWaterAreas'), // 14.1 Pohjavesialueet
    getSharedLayer('drainageAreas'), // 14.2 Valuma-alueet
    getSharedLayer('basinOutlets'), // 14.3 Purkupisteet
  ];
}
